/**
 * It Is Interesting Portal - Main Component
 *
 * Корпоративный портал управления организациями
 * - Mantine UI Components
 * - Custom theme (portalTheme)
 * - Design tokens для консистентного дизайна
 * - React Router для навигации
 */

import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { MantineProvider, AppShell } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { ModalsProvider } from '@mantine/modals';
import { DatesProvider } from '@mantine/dates';
import 'dayjs/locale/ru';
import { portalTheme } from './theme/portalTheme';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import APITokensPage from './components/Settings/APITokensPage';
import UsersPage from './components/Settings/Users/UsersPage';
import MessageTemplatesPage from './pages/MessageTemplatesPage';
import SubjectsPage from './pages/SubjectsPage';
import PricingPage from './pages/PricingPage';
import PaymentsSettingsPage from './pages/PaymentsSettingsPage';
import CompetitionsPage from './pages/CompetitionsPage';
import PartnerPositionsPage from './pages/PartnerPositionsPage';
import PartnershipStatusesPage from './pages/PartnershipStatusesPage';
import { SessionProvider } from './context/SessionContext';
import LoginPage from './pages/LoginPage';
import Organizations from './pages/Organizations';
import OrganizationDetails from './pages/OrganizationDetails';
import ContactsPage from './components/Contacts/ContactsPage';
import Students from './pages/Students';
import Parents from './pages/Parents';
import CalendarPage from './pages/CalendarPage';
import ProtectedRoute from './components/ProtectedRoute';
import PermissionRoute from './components/PermissionRoute';
import { SalesDashboard } from './components/Dashboard';
import ReportsPage from './pages/ReportsPage';
import PublicRequisitesPage from './pages/PublicRequisitesPage';
import Schedule from './pages/Schedule';

// Protected Layout with AppShell (Header + Sidebar)
function ProtectedLayout({ children }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpened, setMobileOpened] = useState(false);

  return (
    <AppShell
      header={{ height: 50 }}
      navbar={{
        width: sidebarCollapsed ? 60 : 280,
        breakpoint: 'sm',
        collapsed: { mobile: !mobileOpened }
      }}
      padding="lg"
    >
      {/* Portal Header */}
      <Header
        mobileOpened={mobileOpened}
        onMobileToggle={() => setMobileOpened(!mobileOpened)}
      />

      {/* Portal Sidebar */}
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        onMobileClose={() => setMobileOpened(false)}
      />

      {/* Main Content */}
      <AppShell.Main>
        {children}
      </AppShell.Main>
    </AppShell>
  );
}

function App() {
  return (
    <Router>
      <MantineProvider theme={portalTheme}>
        <DatesProvider settings={{ locale: 'ru' }}>
          <Notifications
            position="top-right"
            autoClose={4000}
            containerWidth={380}
            notificationMaxHeight={200}
            styles={{
              root: {
                // Контейнер уведомлений
              },
              notification: {
                // Само уведомление - скруглённые углы как у виджетов
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              }
            }}
          />
          <ModalsProvider>
            <SessionProvider>
              <Routes>
                {/* Public Routes - без Header/Sidebar */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/public/requisites/:token" element={<PublicRequisitesPage />} />

                {/* Protected Routes - с Header/Sidebar */}
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <ProtectedLayout>
                        <Routes>
                          <Route path="/" element={<SalesDashboard />} />
                          <Route
                            path="/organizations"
                            element={
                              <PermissionRoute permission="organizations:read">
                                <Organizations />
                              </PermissionRoute>
                            }
                          />
                          <Route
                            path="/organizations/:id"
                            element={
                              <PermissionRoute permission="organizations:read">
                                <OrganizationDetails />
                              </PermissionRoute>
                            }
                          />
                          <Route
                            path="/contacts"
                            element={
                              <PermissionRoute permission="contacts:read">
                                <ContactsPage />
                              </PermissionRoute>
                            }
                          />
                          <Route
                            path="/students"
                            element={
                              <PermissionRoute permission="students:read">
                                <Students />
                              </PermissionRoute>
                            }
                          />
                          <Route
                            path="/parents"
                            element={
                              <PermissionRoute permission="parents:read">
                                <Parents />
                              </PermissionRoute>
                            }
                          />
                          <Route
                            path="/calendar"
                            element={
                              <PermissionRoute permission="calendar:read">
                                <CalendarPage />
                              </PermissionRoute>
                            }
                          />
                          <Route
                            path="/schedule"
                            element={
                              <PermissionRoute permission="groups:read">
                                <Schedule />
                              </PermissionRoute>
                            }
                          />
                          <Route
                            path="/competitions"
                            element={
                              <PermissionRoute permission="groups:read">
                                <CompetitionsPage />
                              </PermissionRoute>
                            }
                          />
                          <Route
                            path="/settings/api-tokens"
                            element={
                              <PermissionRoute permission="settings:read">
                                <APITokensPage />
                              </PermissionRoute>
                            }
                          />
                          <Route
                            path="/settings/users"
                            element={
                              <PermissionRoute permission="users:read">
                                <UsersPage />
                              </PermissionRoute>
                            }
                          />
                          <Route
                            path="/settings/message-templates"
                            element={
                              <PermissionRoute permission="settings:read">
                                <MessageTemplatesPage />
                              </PermissionRoute>
                            }
                          />
                          <Route
                            path="/settings/subjects"
                            element={
                              <PermissionRoute permission="settings:read">
                                <SubjectsPage />
                              </PermissionRoute>
                            }
                          />
                          <Route
                            path="/settings/pricing"
                            element={
                              <PermissionRoute permission="settings:read">
                                <PricingPage />
                              </PermissionRoute>
                            }
                          />
                          <Route
                            path="/settings/payments"
                            element={
                              <PermissionRoute permission="settings:read">
                                <PaymentsSettingsPage />
                              </PermissionRoute>
                            }
                          />
                          <Route
                            path="/settings/partner-positions"
                            element={
                              <PermissionRoute permission="settings:read">
                                <PartnerPositionsPage />
                              </PermissionRoute>
                            }
                          />
                          <Route
                            path="/settings/partnership-statuses"
                            element={
                              <PermissionRoute permission="settings:read">
                                <PartnershipStatusesPage />
                              </PermissionRoute>
                            }
                          />
                          <Route
                            path="/reports/dashboards"
                            element={
                              <PermissionRoute permission="reports:read">
                                <ReportsPage />
                              </PermissionRoute>
                            }
                          />
                        </Routes>
                      </ProtectedLayout>
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </SessionProvider>
          </ModalsProvider>
        </DatesProvider>
      </MantineProvider>
    </Router>
  );
}

export default App;
